import { AbstractTask, TaskStatus, TaskType } from "./task.ts";
import { loadFFmpegTask } from "../ffmpeg.ts";
import { ffmpegRemoveDir } from "./ffmpeg.ts";
import { FFFSType, type FileData, type LogEvent } from "@ffmpeg/ffmpeg";
import { downloadFile } from "../../utils/file.ts";
import { EncodeVideoTaskWebCodecs } from "./webcodecs_encode_task.ts";

// This is the old FFmpeg task, renamed
class EncodeVideoTaskFFmpeg extends AbstractTask {
    #imgs: File[];
    #q: number;
    #spd: number;
    #fps: number;
    #hl: number;
    #outputFileData: FileData = "";
    #outputFileName = "output.mp4";
    #outputMimeType = "video/mp4";
    #audioFile: File | null;

    constructor(fileObjectList: File[], quality = 31, speed = 0, fps = 25, heightLimit = -1, audioFile: File | null = null) {
        super(TaskType.EncodeVideo, fileObjectList.length, "");
        this.#imgs = [...fileObjectList];
        this.#fps = fps;
        this.#q = quality; // [0, 63]
        this.#spd = speed; // [-16, 16]
        this.#hl = heightLimit;
        this.#audioFile = audioFile;
    }
    override _action = async (abortSignal: AbortSignal) => {
        await loadFFmpegTask.reload();
        const ffmpeg = loadFFmpegTask.getFFmpeg();
        
        // Clean up previous run if any
        await ffmpegRemoveDir(ffmpeg, "src");
        
        // prepare image list
        await ffmpeg.createDir("src");
        const fileList = [];
        for (const f of this.#imgs) {
            fileList.push(f.name);
        }
        const fileListText = [
            "ffconcat version 1.0",
            ...fileList.map((fn) => `file '${fn}'\nduration ${1 / this.#fps}\n`),
        ].join("\n");
        const fileListFile = new File([new TextEncoder().encode(fileListText).buffer as ArrayBuffer], "pics.txt");
        this.#imgs.push(fileListFile);

        // Mount files (Images + Optional Audio)
        const mountFiles: File[] = [...this.#imgs];
        let audioFileName = "";
        if (this.#audioFile) {
            // Rename to avoid spaces or special chars issues
            const ext = this.#audioFile.name.split('.').pop() || "mp3";
            audioFileName = `bgm.${ext}`;
            // Create a new file object with the safe name
            const safeAudioFile = new File([this.#audioFile], audioFileName, { type: this.#audioFile.type });
            mountFiles.push(safeAudioFile);
        }
        
        await ffmpeg.mount(FFFSType.WORKERFS, { files: mountFiles }, "src");
        console.log("Files mounted to src/");
        
        abortSignal.throwIfAborted();
        // excute ffmpeg command
        const logs: string[] = [];
        const onLogEvent = (logEvent: LogEvent) => {
            // parse output to progress
            console.log(logEvent.message);
            logs.push(logEvent.message);
            if (logs.length > 20) logs.shift(); // Keep last 20 lines

            const REG = /.*frame=\s*(\d+)\s.*/;
            const result = REG.exec(logEvent.message);
            if (result) {
                const curr = Number.parseInt(result[1]);
                if (Number.isInteger(curr)) {
                    this.$store.setKey("current", curr);
                }
            }
            if (abortSignal.aborted) {
                ffmpeg.terminate();
            }
        };
        try {
            ffmpeg.on("log", onLogEvent);
            
            const inputArgs = [
                "-f", "concat",
                "-safe", "0",
                "-i", "src/pics.txt",
            ];
            
            // Add Audio Input if exists
            if (this.#audioFile) {
                inputArgs.push("-i", `src/${audioFileName}`);
            }
            
            const filterArgs: string[] = [];
            if (this.#hl > 0) {
                filterArgs.push("-vf", `scale=-2:${this.#hl}`);
            }

            // Map streams: Video from input 0, Audio from input 1 (if exists)
            const mapArgs: string[] = ["-map", "0:v"];
            if (this.#audioFile) {
                mapArgs.push("-map", "1:a");
                // Cut audio to video length
                mapArgs.push("-shortest");
                // Ensure AAC encoding for compatibility
                mapArgs.push("-c:a", "aac");
            }

            // Strategy 1: H.264 (Best Quality/Compatibility)
            let success = false;
            try {
                console.log("Trying H.264 encoding...");
                this.$store.setKey("info", "Encoding (H.264)...");
                const cmd = [
                    ...inputArgs,
                    ...mapArgs,
                    "-c:v", "libx264",
                    "-preset", "ultrafast",
                    "-crf", this.#q.toString(),
                    "-pix_fmt", "yuv420p",
                    ...filterArgs,
                    "output.mp4"
                ];
                
                const ret = await ffmpeg.exec(cmd);
                if (ret === 0) {
                    success = true;
                } else {
                    logs.push(`H.264 exit code: ${ret}`);
                }
            } catch (e) {
                console.warn("H.264 encoding failed, falling back to MPEG-4", e);
                logs.push(`H.264 exception: ${e}`);
            }

            // Strategy 2: MPEG-4 (Fallback)
            if (!success) {
                console.log("Trying MPEG-4 encoding...");
                this.$store.setKey("info", "Encoding (MPEG-4 Fallback)...");
                // Map quality: user q (15-50) -> mpeg4 q (1-31, lower is better)
                // New Formula: aggressive scaling to improve quality
                // Input 15 (best) -> 1 (Highest possible quality for MPEG4)
                // Input 23 (default) -> 3
                // Input 50 (worst) -> 10
                const mpeg4Q = Math.max(1, Math.min(31, Math.floor((this.#q - 12) / 3.5)));
                
                // Increase bitrate target significantly for MPEG-4 to avoid blockiness
                // 50Mbps -> 80Mbps (Near Lossless)
                const cmd = [
                    ...inputArgs,
                    ...mapArgs,
                    "-c:v", "mpeg4",
                    "-q:v", mpeg4Q.toString(),
                    "-b:v", "80000k", 
                    ...filterArgs,
                    "output.mp4"
                ];

                const ret = await ffmpeg.exec(cmd);
                if (ret !== 0) {
                    throw new Error(`All encoding attempts failed. Last logs:\n${logs.join("\n")}`);
                }
            }

            this.#outputFileName = "output.mp4";
            this.#outputMimeType = "video/mp4";

            // Read output file immediately after success, before any cleanup that might crash the worker
            try {
                 this.#outputFileData = await ffmpeg.readFile(this.#outputFileName);
                 await ffmpeg.deleteFile(this.#outputFileName);
            } catch (e) {
                 throw new Error(`Failed to read output file. Last logs:\n${logs.join("\n")}`);
            }

        } finally {
            ffmpeg.off("log", onLogEvent);
            // unmount
            try {
                await ffmpeg.unmount("src");
            } catch (e) {
                console.warn("Unmount failed", e);
            }
            // cleanup
            await ffmpegRemoveDir(ffmpeg, "src");
        }
        abortSignal.throwIfAborted();
    };
    downloadOutputFile() {
        downloadFile([this.#outputFileData as BlobPart], this.#outputFileName);
    }
    getOutputBlob() {
        return new Blob([this.#outputFileData as BlobPart], { type: this.#outputMimeType });
    }
}

// Proxy class that decides which implementation to use
export class EncodeVideoTask extends AbstractTask {
    #imgs: File[];
    #q: number;
    #spd: number;
    #fps: number;
    #hl: number;
    #outputBlob: Blob | null = null;
    #useWebCodecs: boolean;
    #audioFile: File | null;

    constructor(fileObjectList: File[], quality = 31, speed = 0, fps = 25, heightLimit = -1, audioFile: File | null = null) {
        super(TaskType.EncodeVideo, fileObjectList.length, "");
        this.#imgs = [...fileObjectList];
        this.#q = quality;
        this.#spd = speed;
        this.#fps = fps;
        this.#hl = heightLimit;
        this.#audioFile = audioFile;
        
        // Feature detection for WebCodecs
        // If audio file is present, we disable WebCodecs and force FFmpeg for mixing
        this.#useWebCodecs = (audioFile === null) && "VideoEncoder" in window && "VideoFrame" in window && "OffscreenCanvas" in window;
    }

    override _action = async (abortSignal: AbortSignal) => {
        if (this.#useWebCodecs) {
            console.log("Attempting WebCodecs encoding...");
            try {
                // Create ephemeral task
                const task = new EncodeVideoTaskWebCodecs(this.#imgs, this.#q, this.#fps, this.#hl);
                // Sync store
                const unsub = task.$store.subscribe(s => {
                    this.$store.setKey("current", s.current);
                    this.$store.setKey("total", s.total);
                    if (s.info) this.$store.setKey("info", s.info);
                });
                
                try {
                    await task._action(abortSignal);
                    if (task.$store.get().status === TaskStatus.Error) {
                        throw new Error(task.$store.get().info || "Video encoding failed");
                    }
                    this.#outputBlob = task.getOutputBlob();
                    unsub();
                    return; // Success
                } catch (e) {
                    unsub();
                    const errMsg = (e as Error).message || "";
                    if (errMsg.includes("not supported")) {
                        console.warn("WebCodecs not supported (runtime check), falling back to FFmpeg.");
                        this.$store.setKey("info", "WebCodecs not supported, switching to FFmpeg...");
                        // Give the UI a moment to show the message (optional, but good for UX)
                        await new Promise(r => setTimeout(r, 500));
                    } else {
                        throw e; // Rethrow other errors
                    }
                }
            } catch (e) {
                 // Should be caught above, but just in case
                 if ((e as Error).message.includes("not supported")) {
                     // proceed to fallback
                 } else {
                     throw e;
                 }
            }
        }

        if (this.#audioFile) {
            console.log("Audio file detected, forcing FFmpeg for mixing");
            this.$store.setKey("info", "Initializing FFmpeg with Audio...");
            // Use FFmpeg logic...
            const task = new EncodeVideoTaskFFmpeg(this.#imgs, this.#q, this.#spd, this.#fps, this.#hl, this.#audioFile);
            const unsub = task.$store.subscribe(s => {
                this.$store.setKey("current", s.current);
                this.$store.setKey("total", s.total);
                if (s.info) this.$store.setKey("info", s.info);
            });
            try {
                await task._action(abortSignal);
                this.#outputBlob = task.getOutputBlob();
            } finally {
                unsub();
            }
            return;
        }

        // Try WebCodecs first
        if (typeof VideoEncoder !== "undefined") {
            console.log("WebCodecs supported, attempting native encoding...");
            this.$store.setKey("info", "Initializing WebCodecs...");
            try {
                const task = new EncodeVideoTaskWebCodecs(this.#imgs, this.#q, this.#fps, this.#hl);
                const unsub = task.$store.subscribe(s => {
                     this.$store.setKey("current", s.current);
                     this.$store.setKey("total", s.total);
                     if (s.info) this.$store.setKey("info", s.info);
                });
                try {
                    await task._action(abortSignal);
                    if (task.$store.get().status === TaskStatus.Error) {
                        throw new Error(task.$store.get().info || "Video encoding failed");
                    }
                    this.#outputBlob = task.getOutputBlob();
                } finally {
                    unsub();
                }
                console.log("WebCodecs encoding successful");
                return; // Success!
            } catch (e) {
                 console.warn("WebCodecs failed, falling back to FFmpeg", e);
                 // Fall through to FFmpeg
            }
        }

        console.log("Using FFmpeg WASM for video encoding (Fallback)");
        this.$store.setKey("info", "Initializing FFmpeg fallback...");
        
        const task = new EncodeVideoTaskFFmpeg(this.#imgs, this.#q, this.#spd, this.#fps, this.#hl, this.#audioFile);
        const unsub = task.$store.subscribe(s => {
            this.$store.setKey("current", s.current);
            this.$store.setKey("total", s.total);
            if (s.info) this.$store.setKey("info", s.info);
        });

        try {
            await task._action(abortSignal);
            this.#outputBlob = task.getOutputBlob();
        } catch (e) {
            console.error("FFmpeg fallback failed", e);
            let msg = "Unknown Error";
            if (e instanceof Error) {
                msg = e.message;
            } else if (typeof e === "string") {
                msg = e;
            } else {
                try {
                    msg = JSON.stringify(e);
                } catch {
                    msg = String(e);
                }
            }
            this.$store.setKey("info", `FFmpeg Error: ${msg}`);
            throw e;
        } finally {
            unsub();
        }
    };

    downloadOutputFile() {
        if (this.#outputBlob) {
            downloadFile([this.#outputBlob], this.#outputBlob.type === "video/mp4" ? "output.mp4" : "output.webm");
        }
    }

    getOutputBlob() {
        return this.#outputBlob || new Blob([], { type: "video/webm" });
    }
}
